import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { PlanType } from 'webapp/enums/enums';

import BillingCompareSubscriptionsComponent from 'webapp/components/billing/compare/subscriptions';

export default class BillingCompareChangesComponent extends Component {
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('navigation') declare navigationService: Service.Navigation;

	get showProfessionalChanges() {
		const planType = this.billingService.activePlan?.planType;
		return !!planType && [PlanType.STARTER, PlanType.PROFESSIONAL].includes(planType);
	}

	get showDataSourcePricingChanges() {
		return !this.isAgency;
	}

	get isGrandfathered() {
		return this.billingService.grandFathered;
	}

	get isProfessional() {
		return !!this.billingService.activePlan?.isProfessional;
	}

	get isYearly() {
		return !!this.billingService.activePlan?.isYearly;
	}

	get isAgency() {
		return !!this.billingService.activePlan?.isAgency;
	}

	get isYearlyStandardProfessional() {
		return this.isYearly && !this.isAgency && this.isProfessional;
	}

	get isYearlyStandardNotProfessional() {
		return this.isYearly && !this.isAgency && !this.isProfessional;
	}

	get isYearlyAgencyProfessional() {
		return this.isYearly && this.isAgency && this.isProfessional;
	}

	get isMonthlyOrQuarterlyStandardProfessional() {
		return !this.isYearly && !this.isAgency && this.isProfessional;
	}

	get isMonthlyOrQuarterlyStandardNotProfessional() {
		return !this.isYearly && !this.isAgency && !this.isProfessional;
	}

	get isMonthlyOrQuarterlyAgencyProfessional() {
		return !this.isYearly && this.isAgency && this.isProfessional;
	}

	@action
	switchToAnnual(): void {
		if (this.billingService.isActiveTrial) {
			this.navigationService.transitionToRoute('settings.billing.plans');
		} else {
			const currentPlanType = this.billingService.activePlan?.planType,
				targetPlan = this.billingService.availablePlans
					.filter((item) => item.planType === currentPlanType)
					.find((item) => item.isYearly);
			if (targetPlan) {
				this.billingService.switchPlan(targetPlan, {
					billingPeriodChangePopup: 'billing_page_cta',
				});
			}
		}
	}
	<template>
		<div class="billing-subscription-changes">

			<h1 class="content-header">Subscription changes</h1>

			{{!--
    <section>
        <p class="content-chunk">On your next bill date after {{this.billingService.migration2024DateFormatted}}, your subscription will be updated to the new pricing shown below.</p>

        <p class="content-chunk">
        	This update reflects the many improvements we’ve made over the past year to help you get even more value from Databox. <a href={{this.billingService.changes2024ReadMoreUrl}} target="blank">Read more</a> about all of the new features and benefits.
        </p>
    </section>
    --}}

			<section class="compare-cost">
				<BillingCompareSubscriptionsComponent
					@previousSubscription={{this.billingService.previousSubscription}}
					@nextSubscription={{this.billingService.nextSubscription}}
				/>
			</section>

			<section class="why">
				<h2 class="content-chunk">Why is my subscription changing?</h2>

				<p class="content-chunk">
					Over the past year, we’ve introduced more features, visualizations, and
					integrations to help you leverage your data to make better decisions. To help us
					continue delivering these improvements, we’re making this slight adjustment to
					our pricing.
				</p>
			</section>

			{{!--
    {{#if this.showProfessionalChanges}}
        <section>
            <h2 class="content-chunk">New Features &amp; Updated Pricing for the Professional Plan</h2>
            <p class="content-chunk">
                We’re adding more new features to the Professional plan and are removing some account limits (on {{this.billingService.migration2024DateFormatted}}), including:
            </p>
            <ul class="content-chunk">
                <li>
                	Create <strong>unlimited custom and calculated metrics</strong>
                </li>
                <li>
                	Invite an <strong>unlimited number of users</strong> to your account
                </li>
                <li>
                	<strong>Add image and text slides</strong> to your reports and present them in full-screen mode
                </li>
                <li>
                	<strong>Visualize ad creatives</strong> on dashboards and reports (available now)
                </li>
                <li>
                	<strong>Upload Excel spreadsheets</strong> directly to Databox (available now)
                </li>
                <li>
                	Instantly <strong>refresh your data on demand</strong> 1x per day (available now)
                </li>
            </ul>
            <p class="content-chunk">To support these additions, the base price of the Professional plan will increase by $30/month (quarterly and annual discounts will still apply).</p>
        </section>
    {{/if}}
    --}}

			{{#if this.showDataSourcePricingChanges}}
				<section>
					<h2 class="content-chunk">Updated pricing for data sources</h2>
					<p class="content-chunk">
						Due to rising processing and data storage costs, the price for each data
						source will increase by $1 (quarterly and annual discounts will still
						apply).
					</p>
				</section>
			{{/if}}

			{{#if this.isGrandfathered}}
				<section>
					<h2 class="content-chunk">Reduction of grandfathered data sources</h2>
					<p class="content-chunk">
						Due to rising processing and data storage costs, we are reducing the number
						of grandfathered free data sources provided to you when we began billing
						separately for data source connections two years ago.
					</p>
				</section>
			{{/if}}
			{{!--
    <section class="lock-in-price">
        <div class="content-chunk">
            <img src="/images/svg/pricing/ticket.svg" width="45" alt="">
        </div>

        <h2 class="content-chunk">Lock in current pricing for 12 months</h2>

        <p class="content-chunk">
            {{#if this.isYearlyStandardProfessional}}
                Renew your annual subscription before {{this.billingService.migration2024DateFormatted}}, to keep your lower price (and lower cost per Data Source). This means you’ll lock in this lower rate for the next 12 months (with pro-rated adjustments) and receive the 20% annual discount.
            {{else if this.isYearlyStandardNotProfessional}}
                Renew your annual subscription before {{this.billingService.migration2024DateFormatted}}, to keep the lower cost per Data Source. This means you’ll lock in this lower rate for the next 12 months (with pro-rated adjustments) and receive the 20% annual discount.
            {{else if this.isYearlyAgencyProfessional}}
                Renew your annual subscription before {{this.billingService.migration2024DateFormatted}}, to keep your lower price. This means you’ll lock in this lower rate for the next 12 months (with pro-rated adjustments) and receive the 20% annual discount.
            {{else if this.isMonthlyOrQuarterlyStandardProfessional}}
                Switch to an annual subscription <strong>before {{this.billingService.migration2024DateFormatted}}</strong>, to keep your lower plan price (and lower cost per Data Source) for the rest of your billing cycle. This means you’ll lock in the lower rate for another year and get a 20% discount on your annual subscription.
            {{else if this.isMonthlyOrQuarterlyStandardNotProfessional}}
                Switch to an annual subscription <strong>before {{this.billingService.migration2024DateFormatted}}</strong>, to keep the lower cost per Data Source for the rest of your billing cycle. This means you’ll lock in the lower rate for another year and get a 20% discount on your annual subscription.
            {{else if this.isMonthlyOrQuarterlyAgencyProfessional}}
                Switch to an annual subscription <strong>before {{this.billingService.migration2024DateFormatted}}</strong>, to keep your lower plan price for the rest of your billing cycle. This means you’ll lock in the lower rate for another year and get a 20% discount on your annual subscription.
            {{/if}}
        </p>
        {{#if this.isGrandfathered}}
            <p class="content-chunk">
                Plus, your current grandfathered Data Source limit will remain the same until the next billing date.
            </p>
        {{/if}}

        {{#if this.isYearly}}
            <p class="content-chunk cta">
                If you have any questions, please <a href={{this.billingService.changes2024BookMeetingUrl}} target="_blank">book a meeting</a> with your Customer Success Manager where you can discuss your account's changes and options for locking in lower prices.
            </p>
        {{else}}
            <p class="content-chunk cta">
                <a role="button" class="button" {{on "click" this.switchToAnnual}}>Switch to annual</a>
            </p>
        {{/if}}
    </section>
    --}}

		</div>
	</template>
}
